import React, { MouseEventHandler, useState } from 'react';
import cn from 'classnames';
import throttle from 'lodash.throttle';

import { isTouchDevice } from 'helpers/ClientUtils';
import COLOR_SWATCH_MAP from 'constants/colorSwatchMap';
import { Product, ProductWithStyles } from 'constants/searchTypes';
import { HeartProps } from 'components/common/Heart';
import Scroller from 'components/common/Scroller';
import ProductCard from 'components/common/ProductCard';
import ProductCardQuickShopLink from 'components/common/ProductCardQuickShopLink';
import { track } from 'apis/amethyst';
import { evSwatchInteract } from 'events/search';

import css from 'styles/components/search/colorswatches.scss';

interface Props extends ProductWithStyles {
  makeSwatchClickHandler: (swatch: Product, interactedSwatchIndex: number) => MouseEventHandler;
}

export const ColorSwatches = ({ relatedStyles, makeSwatchClickHandler, colorId: mainColorId, styleId: mainStyleId }: Props) => {
  if (!relatedStyles?.length) {
    return null;
  }

  const styleMarkup = relatedStyles.map((swatch, i) => {
    const { colorId, styleId, styleColor, swatchUrl, color } = swatch;
    const isActive = !!(mainColorId === colorId && mainStyleId === styleId);
    const colorName = COLOR_SWATCH_MAP[color?.toLowerCase()] || 'unknown';

    return (
      <li key={colorId}>
        <button
          title={styleColor}
          type="button"
          onMouseEnter={makeSwatchClickHandler(swatch, i)}
          onClick={makeSwatchClickHandler(swatch, i)}
          data-test-id="colorSwatchButton">
          {swatchUrl ?
            <img className={cn({ [css.active]: isActive })} src={swatchUrl} alt={color}/>
            :
            <span className={cn({ [css.unknown]: colorName === 'unknown', [css.active]: isActive })} style={{ background: colorName }}/>
          }
        </button>
      </li>
    );
  });

  return <Scroller>{({ contentRef, leftArrowClick, rightArrowClick, arrowsVisible, scrollLeftDisabled, scrollRightDisabled }) =>
    (
      <section className={cn(css.wrapper, { [css.hasArrows]: arrowsVisible })}>
        <button
          type="button"
          className={css.leftArrow}
          disabled={scrollLeftDisabled}
          data-test-id="scrollLeftButton"
          onClick={leftArrowClick}>Scroll Left</button>
        <ul ref={contentRef} className={css.contentClass} data-test-id="colorSwatch">
          {styleMarkup}
        </ul>
        <button
          type="button"
          className={css.rightArrow}
          disabled={scrollRightDisabled}
          data-test-id="scrollRightButton"
          onClick={rightArrowClick}>Scroll Right</button>
      </section>
    )}</Scroller>;
};

interface WrapperProps extends ProductWithStyles {
  testId: string;
  animationTimerIndex: number;
  heartsInfo: HeartProps;
  onProductMediaHovered: (hoveredProduct: Product, mainStyleId: string) => void;
}

const ColorSwatchWrapper = ({ testId, animationTimerIndex, heartsInfo, onProductMediaHovered, ...mainProduct }: WrapperProps) => {
  const [currentProduct, setCurrentProduct] = useState<Product>(mainProduct);
  const { styleId } = mainProduct;

  const makeSwatchClickHandler = (swatch: Product, interactedSwatchIndex: number) => throttle(e => {
    const touchDevice = isTouchDevice();
    const newProduct = { ...mainProduct, ...swatch };
    if (!touchDevice && e.type === 'mouseenter' || touchDevice && e.type === 'click') {
      const interactionType = touchDevice ? 'CLICK' : 'HOVER';
      track(() => ([
        evSwatchInteract, {
          mainStyleId: styleId,
          interactedProduct: swatch,
          interactedSwatchIndex,
          interactionType
        }
      ]));
    }
    setCurrentProduct(newProduct);
  }, 500);

  const makeProductMediaHoverHandler = () => {
    onProductMediaHovered(currentProduct, styleId);
  };

  return <ProductCard
    {...currentProduct}
    onMediaHovered={makeProductMediaHoverHandler}
    data-test-id={testId}
    hearts={heartsInfo}
    CardDetailsTopSlot={ColorSwatches}
    CardBottomSlot={ProductCardQuickShopLink}
    makeSwatchClickHandler={makeSwatchClickHandler}
    animationTimerIndex={animationTimerIndex}/>;
};

export default ColorSwatchWrapper;
